import useDebouncedCallback from '@src/hooks/useDebouncedCallback';
import useNoRenderRef from '@src/hooks/useNoRenderRef';
import useThrottledCallback from '@src/hooks/useThrottledCallback';
import React, { createContext, useCallback, useRef, useState } from 'react';

interface Props {
  children: React.ReactElement;
}

export interface ImageData {
  url: string;
  naturalDimensions: { height: number; width: number };
  loaded: boolean;
  widthPercentage?: number;
}

export type ImageGroup = Array<ImageData>;

interface State {
  insertionTarget: string | null;
  images: ImageGroup;
  isLoadingImages: boolean;
  dragging: boolean;
}

interface Dispatch {
  addImageByUrl: (url: string) => void;
  clearInsertionTarget: () => void;
  setInsertionTarget: (id: string) => void;
  startDragging: () => void;
  stopDragging: () => void;
}

const defaultStateValue = {
  insertionTarget: null,
  images: [],
  isLoadingImages: false,
  dragging: false
};
const defaultDispatchValue = {
  addImageByUrl: () => {},
  clearInsertionTarget: () => {},
  setInsertionTarget: () => {},
  startDragging: () => {},
  stopDragging: () => {}
};

export const ImageCacheStateContext = createContext<State>(defaultStateValue);
export const ImageCacheDispatchContext = createContext<Dispatch>(
  defaultDispatchValue
);

export const ImageCacheProvider = ({ children }: Props) => {
  const imagesRef = useRef<ImageGroup>([]);
  const [images, setImages] = useState<ImageGroup>([]);
  const [dragging, setDragging] = useState(false);
  const [insertionTarget, setInsertionTarget] = useState<string | null>(null);
  const [loadingImagesCount, setLoadingImagesCount] = useState(0);

  const rerenderImages = useDebouncedCallback(
    () => setImages(imagesRef.current),
    [],
    250
  );

  const storeImage = useCallback(
    (imageEl: HTMLImageElement) => {
      imagesRef.current = [
        ...imagesRef.current,
        {
          url: imageEl.src,
          naturalDimensions: {
            height: imageEl.naturalHeight,
            width: imageEl.naturalWidth
          },
          loaded: true
        }
      ];

      rerenderImages();
    },
    [rerenderImages]
  );

  const decrementLoadingCount = useCallback(() => {
    setTimeout(() => setLoadingImagesCount((current) => current - 1), 500);
  }, []);

  const incrementLoadingCount = useCallback(() => {
    setLoadingImagesCount((current) => current + 1);
  }, []);

  const addImageByUrl = useCallback(
    (url: string) => {
      const image = new Image();

      image.onload = () => {
        storeImage(image);
        decrementLoadingCount();
      };

      image.onerror = decrementLoadingCount;

      incrementLoadingCount();
      image.src = url;
    },
    [decrementLoadingCount, incrementLoadingCount, storeImage]
  );

  const clearInsertionTarget = useCallback(() => setInsertionTarget(null), []);

  return (
    <ImageCacheDispatchContext.Provider
      value={{
        addImageByUrl,
        clearInsertionTarget,
        setInsertionTarget,
        startDragging: () => setDragging(true),
        stopDragging: () => setDragging(false)
      }}
    >
      <ImageCacheStateContext.Provider
        value={{
          images,
          insertionTarget,
          dragging,
          isLoadingImages: loadingImagesCount > 0
        }}
      >
        {children}
      </ImageCacheStateContext.Provider>
    </ImageCacheDispatchContext.Provider>
  );
};
